import { FieldModel, UserId } from '@cibo/core'
import { LandManagerToolbar, useLandManagement } from '@cibo/landmanager'
import {
  AuthUserPermission,
  Can,
  USER_DATA_KEYS,
  useAuth,
  useDataGridPersistence,
  useWindowTitle,
} from '@cibo/profile'
import { Divider, Paper, Stack } from '@mui/material'
import { GridRowId } from '@mui/x-data-grid-pro'
import { FieldTable } from 'components'
import { EnrollSelectedFields } from 'components/EnrollSelectedFields'
import { LayoutSection } from 'components/ImpactLayout'
import { ProfileSelectedFieldsButton } from 'components/ProfileSelectedFieldsButton'
import { PendingAgreementsAlert } from 'features/Agreements'
import { BulkForceEnrollButton } from 'features/debug/BulkForceEnrollButton'
import { GenerateTestAgreementButton } from 'features/debug/GenerateTestAgreementButton'
import ROUTE, {
  ACCOUNT_ROUTES,
  ENROLLMENT_ROUTES,
  LAND_MANAGER_ROUTES,
  PROGRAM_ROUTES,
} from 'navigation/types/routes'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, generatePath, useLocation, useNavigate } from 'react-router-dom'
import { ProfileFieldsAlert } from '../../components/ProfileFieldsAlert'
import { ControlPlotsAlert } from '../../features/ControlGroups/ControlPlotsAlert'
import { BulkAssignOwnerButton } from '../../features/debug/BulkAssignOwner'
import { ENGAGEMENT_PATHS } from '../../navigation/types/engagementRoutes'
import { OnboardingLayout } from '../Home/OnboardingLayout'
import { useColumnSets } from './useColumnSets'
import { useConfirmOpportunityDialog } from './useConfirmOpportunity'
import { useFieldTableActionsColumn } from './useFieldTableActionsColumn'

export const LandManager = () => {
  const { isLoggedIn, userId } = useAuth()
  const { t } = useTranslation('impact/pages/LandManager')
  useWindowTitle(t('pageTitle'))
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const { openFieldMetaDialog } = useLandManagement()

  const { confirmOpportunityDialog, setConfirmOpportunity } = useConfirmOpportunityDialog()

  const onClickNeedsInfo = (field: FieldModel) =>
    navigate(generatePath(ROUTE.PROFILE_FIELD, { fieldId: field.resourceId }))

  const { programStatusColumns } = useColumnSets({
    onClickNeedsInfo,
    onClickOpportunity: ({ programId, field }) => setConfirmOpportunity({ programId, field }),
    onClickEngaged: ({
      engagementId,
      programId,
      userId,
    }: {
      engagementId: string
      programId: string
      userId: UserId
    }) => {
      navigate(
        generatePath(`${PROGRAM_ROUTES.ENGAGEMENTS}/${ENGAGEMENT_PATHS.ENGAGEMENT}`, {
          engagementId,
          programId,
        })
      )
    },
    onClickEnrolling: ({ workflowId }: { workflowId: string }) => {
      navigate(generatePath(ENROLLMENT_ROUTES.DETAIL, { workflowId }))
    },
    onClickProgram: (programId: string) =>
      navigate(generatePath(PROGRAM_ROUTES.DETAIL, { programId })),
  })

  const onEditMeta = (resourceId: GridRowId) =>
    openFieldMetaDialog({ resourceId: resourceId as string })

  const actionsColumn = useFieldTableActionsColumn({ onEditMeta })
  const columns = useMemo(() => [actionsColumn, ...programStatusColumns], [
    actionsColumn,
    programStatusColumns,
  ])

  const datagridProps = useDataGridPersistence({
    columns,
    key: USER_DATA_KEYS.FIELDS_TABLE_GLOBAL,
    options: ['columnOrder', 'columnVisibility', 'pageSize', 'columnWidth'],
  })

  if (!isLoggedIn) {
    return <Navigate replace to="/" />
  }

  const baseQuery = {
    owner: pathname === LAND_MANAGER_ROUTES.PERSONAL ? userId : undefined,
  }

  return (
    <OnboardingLayout>
      <LayoutSection autoHeight>
        <Stack spacing={2}>
          <PendingAgreementsAlert onClick={() => navigate(ACCOUNT_ROUTES.AGREEMENTS)} />
          <ProfileFieldsAlert onClick={() => navigate(ROUTE.PROFILE_FIELDS)} />
          <ControlPlotsAlert />
        </Stack>

        <Paper elevation={2}>
          <FieldTable
            {...datagridProps}
            components={{
              BulkActions: props => (
                <Stack direction="row" divider={<Divider orientation="vertical" />} spacing={2}>
                  <LandManagerToolbar {...props} />
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <ProfileSelectedFieldsButton variant="contained" size="small" color="warning" />
                    <EnrollSelectedFields buttonProps={{ variant: 'contained', size: 'small' }} />
                    <Can useAny={[AuthUserPermission.DEBUG_USER]}>
                      <GenerateTestAgreementButton />
                      <BulkForceEnrollButton />
                      <BulkAssignOwnerButton />
                    </Can>
                  </Stack>
                </Stack>
              ),
            }}
            baseQuery={baseQuery}
          />
        </Paper>
      </LayoutSection>

      {confirmOpportunityDialog}
    </OnboardingLayout>
  )
}
